import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  FilteringState,
  GroupingState,
  IntegratedGrouping,
  IntegratedPaging,
  CustomPaging,
  IntegratedSorting,
  IntegratedFiltering,
  PagingState,
  SortingState,
  DataTypeProvider,
  SummaryState,
  IntegratedSummary,
  IntegratedSelection,
  SelectionState,
} from "@devexpress/dx-react-grid";
import {
  ExportPanel,
  Grid,
  DragDropProvider,
  GroupingPanel,
  PagingPanel,
  TableFilterRow,
  TableGroupRow,
  TableSummaryRow,
  TableHeaderRow,
  Toolbar,
  TableSelection,
  VirtualTable,
  ColumnChooser,
  TableColumnVisibility,
  TableColumnResizing,
} from "@devexpress/dx-react-grid-material-ui";
import { IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import { GridExporter } from "@devexpress/dx-react-grid-export";
import { makeStyles } from "@material-ui/core/styles";
import ProjectButton from "./ProjectButton";
import { dataService } from "../../../../Services/data/data.service";
import { toast } from "react-toastify";

import saveAs from "file-saver";
import { BadgeCheck, BadgeMinus, Plus, Minus, ChevronUp, ChevronDown, Anchor, Pin } from "lucide-react";

import { resultTableColumnExtensions, resultGroupingColumnExtensions, filteringStateColumnExtensions, resultColumns, controlTypes, externalApiTypes, externalApiEnumBehaviorTypes } from "../../../../Config/constants";
import { getCurrentDateTimeString, formatDate, formatToTimeString, formatToTimeStringHHMMSS } from "../../../../Utils/dateHelper";
import Tooltip from "../../../Shared/Tooltip";
import { PriceCell, useBundleMaps, useMeasurementBreakdownCache } from "../../../Shared/PriceBreakdown";

// ─── Column resizing / visibility helpers ─────────────────────────────────────
const DEFAULT_COLUMN_WIDTH = 180;
const COL_WIDTH_STORAGE_PREFIX = "taplr.colWidths.";
const HIDDEN_COLS_STORAGE_PREFIX = "taplr.hiddenCols.";

const buildDefaultColumnWidths = (columns) =>
  (columns || []).map((c) => {
    const ext = resultTableColumnExtensions.find((e) => e.columnName === c.name);
    return { columnName: c.name, width: (ext && ext.width) || DEFAULT_COLUMN_WIDTH };
  });

const loadStoredColumnWidths = (tableId) => {
  if (!tableId) return null;
  try {
    const raw = localStorage.getItem(COL_WIDTH_STORAGE_PREFIX + tableId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const loadStoredHiddenColumns = (tableId) => {
  if (!tableId) return null;
  try {
    const raw = localStorage.getItem(HIDDEN_COLS_STORAGE_PREFIX + tableId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export default function DevExDataTable(props) {
  const [grouping, setGrouping] = useState([]);
  const [data, setData] = useState([]);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [image, setImage] = useState("");
  const [description, setDescription] = useState("");
  const [currency, setCurrency] = useState({ symbol: "€", name: "Euro" });
  const [groupSortingByLevel, setGroupSortingByLevel] = useState({}); // Object to store sorting per level: { 0: { property: 'Counter total', direction: 'asc' }, 1: { property: 'eventCount', direction: 'desc' } }
  const [sorting, setSorting] = useState([]); // DevExtreme sorting state
  const [isGroupSorted, setIsGroupSorted] = useState(false); // Track if data is sorted by group values

  // Column widths (resizable). Seeded from localStorage when props.tableId is provided.
  const [columnWidths, setColumnWidths] = useState(() => {
    const defaults = buildDefaultColumnWidths(props.columns);
    const stored = loadStoredColumnWidths(props.tableId);
    if (!stored) return defaults;
    return defaults.map((d) => {
      const s = stored.find((x) => x.columnName === d.columnName);
      return s && typeof s.width === "number" ? { columnName: d.columnName, width: s.width } : d;
    });
  });
  const persistWidthsTimeoutRef = useRef(null);

  // Hidden columns (controlled). Seeded from localStorage when props.tableId is provided,
  // otherwise from props.defaultHiddenColumnNames.
  const [hiddenColumnNames, setHiddenColumnNames] = useState(() => {
    const stored = loadStoredHiddenColumns(props.tableId);
    if (stored) return stored;
    return props.defaultHiddenColumnNames || [];
  });
  const persistHiddenColsTimeoutRef = useRef(null);

  const handleHiddenColumnNamesChange = (next) => {
    // When columns are hidden, give their widths to the last remaining visible column
    // so that no empty space opens up to the right of the table.
    const newlyHidden = next.filter((col) => !hiddenColumnNames.includes(col));
    if (newlyHidden.length > 0) {
      const visibleAfter = (props.columns || []).map((c) => c.name).filter((name) => !next.includes(name));
      if (visibleAfter.length > 0) {
        const lastVisible = visibleAfter[visibleAfter.length - 1];
        const extraWidth = newlyHidden.reduce((sum, colName) => {
          const cw = columnWidths.find((w) => w.columnName === colName);
          return sum + (cw ? cw.width : DEFAULT_COLUMN_WIDTH);
        }, 0);
        handleColumnWidthsChange(columnWidths.map((w) => (w.columnName === lastVisible ? { ...w, width: w.width + extraWidth } : w)));
      }
    }

    setHiddenColumnNames(next);
    if (!props.tableId) return;
    if (persistHiddenColsTimeoutRef.current) clearTimeout(persistHiddenColsTimeoutRef.current);
    persistHiddenColsTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(HIDDEN_COLS_STORAGE_PREFIX + props.tableId, JSON.stringify(next));
      } catch {
        /* ignore quota / serialization errors */
      }
    }, 250);
  };

  // Keep columnWidths in sync when the set of columns changes (add/remove).
  useEffect(() => {
    setColumnWidths((prev) => {
      const defaults = buildDefaultColumnWidths(props.columns);
      return defaults.map((d) => {
        const existing = prev.find((p) => p.columnName === d.columnName);
        return existing ? { columnName: d.columnName, width: existing.width } : d;
      });
    });
  }, [props.columns]);

  const handleColumnWidthsChange = (next) => {
    setColumnWidths(next);
    if (!props.tableId) return;
    if (persistWidthsTimeoutRef.current) clearTimeout(persistWidthsTimeoutRef.current);
    persistWidthsTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(COL_WIDTH_STORAGE_PREFIX + props.tableId, JSON.stringify(next));
      } catch {
        /* ignore quota / serialization errors */
      }
    }, 250);
  };

  const tableWrapperRef = useRef(null);

  // Ensure the last visible column always stretches to fill any leftover container width.
  // Runs on mount and whenever the container is resized or visible columns change.
  const fillContainerGap = useCallback(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    const containerWidth = el.offsetWidth;
    const visibleNames = (props.columns || []).map((c) => c.name).filter((name) => !hiddenColumnNames.includes(name));
    if (visibleNames.length === 0) return;
    setColumnWidths((prev) => {
      const totalWidth = visibleNames.reduce((sum, name) => {
        const cw = prev.find((w) => w.columnName === name);
        return sum + (cw ? cw.width : DEFAULT_COLUMN_WIDTH);
      }, 0);
      const gap = containerWidth - totalWidth;
      if (Math.abs(gap) <= 1) return prev;
      const lastVisible = visibleNames[visibleNames.length - 1];
      return prev.map((w) => (w.columnName === lastVisible ? { ...w, width: Math.max(50, w.width + gap) } : w));
    });
  }, [hiddenColumnNames, props.columns]);

  useEffect(() => {
    fillContainerGap();
  }, [fillContainerGap]);

  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(fillContainerGap);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillContainerGap]);

  const classes = useStyles();

  // Shared price-breakdown plumbing: bundle/projection lookup maps + per-measurement
  // junction breakdown cache. See `Shared/PriceBreakdown.jsx`.
  const { bundleById, bundleTotalsById, projectedPriceByDefId } = useBundleMaps(
    props.allPriceBundles,
    props.activeProjection
  );
  const { cache: measurementBreakdownCache, ensure: ensureMeasurementBreakdown } = useMeasurementBreakdownCache();

  useEffect(() => {
    setData(props.data);
  }, [props.data]);
  useEffect(() => {
    if (props.currency) {
      setCurrency(props.currency);
    }
  }, [props.currency]);

  // Helper functions for calculations
  const calculateCounterTotal = (rows) => {
    const counterRows = rows.filter((x) => x.typeId === controlTypes.EXTERNALAPI && x.externalApiTypeId === externalApiTypes.NUMERIC);
    const oldCounterRows = rows.filter((x) => x.typeId === controlTypes.COUNTER);

    let counterTotal = 0;
    let oldCounterTotal = 0;

    counterRows.forEach((x) => {
      if (x.isNegative) {
        counterTotal = counterTotal - Math.abs(x.measurementValue);
      } else {
        counterTotal = counterTotal + x.measurementValue;
      }
    });

    let positiveCounterRows = oldCounterRows.filter((x) => !x.isNegative);
    let negativeCounterRows = oldCounterRows.filter((x) => x.isNegative);
    oldCounterTotal = positiveCounterRows.length - negativeCounterRows.length;

    return counterTotal + oldCounterTotal;
  };

  const calculateTotalTime = (rows) => {
    const timerRows = rows.filter((x) => x.typeId === controlTypes.STOPWATCH || (x.typeId === controlTypes.EXTERNALAPI && x.externalApiTypeId === externalApiTypes.STOPWATCH));
    return timerRows.reduce((total, x) => total + x.integerValue, 0);
  };

  const calculateTimeElapsed = (rows) => {
    if (rows.length === 0) return 0;

    const earliestStartTime = rows.reduce((prev, current) => (prev.startTime < current.startTime ? prev : current));
    const latestStopTime = rows.reduce((prev, current) => (prev.stopTime > current.stopTime ? prev : current));
    const latestStartTime = rows.reduce((prev, current) => (prev.startTime > current.startTime ? prev : current));

    const latestTime = latestStopTime.stopTime > latestStartTime.startTime ? latestStopTime.stopTime : latestStartTime.startTime;
    return (new Date(latestTime) - new Date(earliestStartTime.startTime)) / 1000;
  };

  const calculateTotalPrice = (rows) => {
    return rows.reduce((acc, item) => {
      if (item.isNegative) {
        return acc + (item.isProfit ? -item.eventPrice : item.eventPrice);
      }
      return acc + (item.isProfit ? item.eventPrice : -item.eventPrice);
    }, 0);
  };
  const calculateTotalProjectedPrice = (rows) => {
    return rows.reduce((acc, item) => {
      if (item.isNegative) {
        return acc + (item.isProfit ? -item.projectedEventPrice : item.projectedEventPrice);
      }
      return acc + (item.isProfit ? item.projectedEventPrice : -item.projectedEventPrice);
    }, 0);
  };

  // Effect to handle group sorting with debouncing for better performance
  useEffect(() => {
    if (Object.keys(groupSortingByLevel).length > 0 && grouping.length > 0) {
      // Use a timeout to debounce rapid sorting changes
      const timeoutId = setTimeout(() => {
        // Convert selection indices to actual row IDs using props.data (original order before sorting)
        // since we're about to re-sort from props.data
        const selection = props.selection || [];
        const selectedIds = selection.map((index) => data[index]?.id).filter((id) => id !== undefined);
        const sortedData = applySortToGroupedData(props.data, grouping, groupSortingByLevel, selectedIds);
        setData(sortedData);
        setIsGroupSorted(true);
      }, 50); // Small delay to batch rapid changes

      return () => clearTimeout(timeoutId);
    } else if (Object.keys(groupSortingByLevel).length === 0) {
      // Reset to original data when sorting is cleared
      setData(props.data);
      setIsGroupSorted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSortingByLevel, grouping, props.data, props.selection]);

  // Clear group sorting when grouping changes
  const handleGroupingChange = (newGrouping) => {
    // Clear custom sorting for levels that no longer exist
    const newGroupSorting = {};
    Object.keys(groupSortingByLevel).forEach((level) => {
      const levelNum = parseInt(level);
      if (levelNum < newGrouping.length) {
        newGroupSorting[level] = groupSortingByLevel[level];
      }
    });
    setGroupSortingByLevel(newGroupSorting);

    // If grouping is completely cleared, reset sorting state
    if (newGrouping.length === 0) {
      setSorting([]);
    }

    setGrouping(newGrouping);
  };

  // Handle DevExtreme sorting changes
  const handleSortingChange = (newSorting) => {
    // Get currently grouped columns
    const groupedColumns = grouping.map((g) => g.columnName);

    // Check if any new sorting is being applied to grouped columns
    const newGroupedColumnSorts = newSorting.filter((sort) => groupedColumns.includes(sort.columnName));

    if (newGroupedColumnSorts.length > 0) {
      // Clear custom group sorting ONLY for the specific levels that have DevExtreme sorting applied
      const newGroupSorting = { ...groupSortingByLevel };
      newGroupedColumnSorts.forEach((sort) => {
        const level = grouping.findIndex((g) => g.columnName === sort.columnName);
        if (level !== -1) {
          // Only clear custom sorting for this specific level
          delete newGroupSorting[level];
        }
      });
      setGroupSortingByLevel(newGroupSorting);
    }

    // Filter out any sorting on grouped columns that have custom sorting for their specific level
    const filteredSorting = newSorting.filter((sort) => {
      const isGroupedColumn = groupedColumns.includes(sort.columnName);
      if (isGroupedColumn) {
        // Find the level of this grouped column
        const level = grouping.findIndex((g) => g.columnName === sort.columnName);
        const hasCustomSort = groupSortingByLevel[level];
        // Only allow DevExtreme sorting if there's no custom sorting for this specific level
        return !hasCustomSort;
      }
      return true; // Allow sorting on non-grouped columns
    });

    setSorting(filteredSorting);
  };

  // Handle selection changes - translate indices when group sorting is active
  const handleSelectionChange = (newSelection) => {
    if (isGroupSorted && props.onSelectionChange) {
      // Convert from sorted data indices to original props.data indices
      // newSelection contains indices into the sorted 'data' array
      // We need to convert them to indices into 'props.data'
      const selectedIds = newSelection.map((sortedIndex) => data[sortedIndex]?.id).filter((id) => id !== undefined);
      const originalIndices = selectedIds
        .map((id) => props.data.findIndex((row) => row.id === id))
        .filter((index) => index !== -1);
      props.onSelectionChange(originalIndices);
    } else if (props.onSelectionChange) {
      props.onSelectionChange(newSelection);
    }
  };

  // Convert incoming selection (props.selection) from original indices to sorted indices
  const getTranslatedSelection = () => {
    if (!isGroupSorted || !props.selection) {
      return props.selection || [];
    }
    // props.selection contains indices into props.data
    // We need to convert them to indices into the sorted 'data' array
    const selectedIds = props.selection.map((originalIndex) => props.data[originalIndex]?.id).filter((id) => id !== undefined);
    return selectedIds
      .map((id) => data.findIndex((row) => row.id === id))
      .filter((index) => index !== -1);
  };

  const translatedSelection = getTranslatedSelection();

  // Memoized calculation functions to avoid recalculating values
  const calculateGroupValues = (rows, property, selectedIds) => {
    const checkedRows = rows.filter((x) => selectedIds.includes(x.id));

    switch (property) {
      case "Counter total":
        return calculateCounterTotal(checkedRows);
      case "Total time":
        return calculateTotalTime(checkedRows);
      case "Time elapsed":
        return calculateTimeElapsed(checkedRows);
      case "totalPrice":
        return calculateTotalPrice(checkedRows);
      case "totalProjectedPrice":
        return calculateTotalProjectedPrice(checkedRows);
      case "eventCount":
        return checkedRows.length;
      default:
        return 0;
    }
  };

  // Function to apply sorting to grouped data recursively for nested grouping
  const applySortToGroupedData = (dataToSort, currentGrouping, groupSortingByLevel, selectedIds, level = 0, parentKeys = []) => {
    if (currentGrouping.length === 0 || level >= currentGrouping.length) return dataToSort;

    const currentColumn = currentGrouping[level].columnName;
    const grouped = groupByColumnName(dataToSort, currentColumn);

    // Get sorting configuration for this level
    const levelSorting = groupSortingByLevel[level];

    if (levelSorting) {
      // Pre-calculate values for all groups to avoid redundant calculations
      const groupValues = new Map();
      const groupKeys = Object.keys(grouped);

      groupKeys.forEach((groupKey) => {
        const groupRows = grouped[groupKey];
        const value = calculateGroupValues(groupRows, levelSorting.property, selectedIds);
        groupValues.set(groupKey, value);
      });

      // Sort groups at current level using pre-calculated values
      const sortedGroups = groupKeys.sort((a, b) => {
        const valueA = groupValues.get(a);
        const valueB = groupValues.get(b);

        if (levelSorting.direction === "asc") {
          return valueA - valueB;
        } else {
          return valueB - valueA;
        }
      });

      const sortedData = [];
      sortedGroups.forEach((groupKey) => {
        // If there are more grouping levels, recursively sort the sub-groups
        if (level + 1 < currentGrouping.length) {
          const newParentKeys = [...parentKeys, groupKey];
          const sortedSubData = applySortToGroupedData(grouped[groupKey], currentGrouping, groupSortingByLevel, selectedIds, level + 1, newParentKeys);
          sortedData.push(...sortedSubData);
        } else {
          sortedData.push(...grouped[groupKey]);
        }
      });

      return sortedData;
    } else {
      // No sorting for this level, but continue to next level
      const sortedData = [];
      Object.keys(grouped).forEach((groupKey) => {
        if (level + 1 < currentGrouping.length) {
          const newParentKeys = [...parentKeys, groupKey];
          const sortedSubData = applySortToGroupedData(grouped[groupKey], currentGrouping, groupSortingByLevel, selectedIds, level + 1, newParentKeys);
          sortedData.push(...sortedSubData);
        } else {
          sortedData.push(...grouped[groupKey]);
        }
      });

      return sortedData;
    }
  };

  //Export excel
  const onSave = (workbook) => {
    var fileName = "registrations_" + getCurrentDateTimeString() + ".xlsx";
    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
    });
  };

  const exporterRef = useRef(null);
  const startExport = useCallback(
    (options) => {
      exporterRef.current.exportGrid(options);
    },
    [exporterRef]
  );

  const tableMessages = {
    sum: "Sum",
    contains: "Contains",
    noData: "No measurements available",
  };

  const startTimeFormatter = (row) => (
    <React.Fragment>
      {row.value !== null && row.value !== "" ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ lineHeight: "1" }}>{formatDate(row.value).split(" ")[0]}</div>
          <div style={{ lineHeight: "1" }}>{formatDate(row.value).split(" ")[1]}</div>
        </div>
      ) : (
        ""
      )}
    </React.Fragment>
  );

  const startHourFormatter = (row) => {
    return <React.Fragment>{row.value !== null && row.value !== "" ? row.value + "h" : ""}</React.Fragment>;
  };

  const startDayOfWeekFormatter = (row) => {
    return <React.Fragment>{row.value !== null && row.value !== "" ? row.value : ""}</React.Fragment>;
  };

  const finishedFormatter = (row) => <React.Fragment>{row.value ? <BadgeMinus style={{ color: "red" }}></BadgeMinus> : <BadgeCheck style={{ color: "green" }}></BadgeCheck>}</React.Fragment>;

  const valueTypeFormatter = (row) => {
    switch (row.value) {
      case "Value added":
        return <div className={classes.chip__green}>Value added</div>;
      case "No value added":
        return <div className={classes.chip__orange}>No value added</div>;
      case "Waste":
        return <div className={classes.chip__red}>Waste</div>;
      default:
        return <div className={classes.chip__grey}>Unknown</div>;
    }
  };

  const valueFormatter = (row) => {
    if (row.row !== undefined) {
      if (row.column.name === "commentText" && row.row !== undefined && row.row.commentImage !== "" && row.row.commentImage !== null && row.row.commentImage !== undefined) {
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {row.value !== null && row.value !== undefined && row.value !== "" && <span>{row.value}</span>}
            <IconButton style={{ borderRadius: "25px" }} onClick={() => handleDialogOpen(row.row.imageId, row.row.description, row.row.commentImage)}>
              <img alt="" style={{ maxWidth: "100%" }} src={"data:image/png;base64," + row.row.commentImage}></img>
            </IconButton>
          </div>
        );
      }
      if (row.value === null || row.value === undefined || row.value === "") {
        if (row.column.name === "measurementValue" && row.row.typeId === controlTypes.STOPWATCH && row.row.integerValue) {
          const totalSeconds = row.row.integerValue;

          // Calculate hours, minutes, and seconds
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;

          // Format each part to ensure two digits
          const formattedTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
          return formattedTime;
        } else {
          return "-";
        }
      } else if (row.row.typeId === controlTypes.CAMERA) {
        return (
          <IconButton style={{ borderRadius: "25px" }} onClick={() => handleDialogOpen(row.row.imageId, row.row.description)}>
            <img alt="" style={{ maxWidth: "100%" }} src={"data:image/png;base64," + row.row.thumbnail}></img>
          </IconButton>
        );
      } else {
        return <span title={row.value}>{row.value}</span>;
      }
    } else {
      if (row.value === null || row.value === undefined || row.value === "") {
        return "-";
      } else {
        return row.value;
      }
    }
  };

  const sessionValuesFormatter = (row) => {
    if (!row.value) return "-";
    const metadata = row.row?.sessionMetadata || [];
    return (
      <Tooltip
        fullwidth={true}
        anchorTo="td"
        content={
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {metadata.map((m, i) => (
              <span key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {m.behaviorTypeId === externalApiEnumBehaviorTypes.ANCHOR && <Anchor size={14} />}
                {m.behaviorTypeId === externalApiEnumBehaviorTypes.STICKY && <Pin size={14} />}
                {m.value}
              </span>
            ))}
          </div>
        }
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{row.value}</span>
      </Tooltip>
    );
  };

  const priceFormatter = (row) => {
    return (
      <PriceCell
        row={row}
        currency={currency}
        classes={classes}
        bundleById={bundleById}
        bundleTotalsById={bundleTotalsById}
        projectedPriceByDefId={projectedPriceByDefId}
        breakdownCache={measurementBreakdownCache}
        ensureBreakdown={ensureMeasurementBreakdown}
      />
    );
  };

  const actionFormatter = (row) => {
    return (
      <ProjectButton data={row.row} details={data.filter((x) => x.projectId === row.row.projectId)}>
        {" "}
      </ProjectButton>
    );
  };

  const handleDialogOpen = (imageId, description, commentImage) => {
    if (imageId !== null && imageId !== undefined && imageId !== 0) {
      dataService
        .GetImageById(imageId)
        .then((response) => {
          setImage(response.image);
          setDescription(description);
          setImageDialogOpen(true);
        })
        .catch((error) => {
          console.log("Error: ", error);
          toast.error(`Something went wrong`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        });
    } else if (commentImage !== "" && commentImage !== undefined && commentImage !== null) {
      setImage(commentImage);
      setDescription(description);
      setImageDialogOpen(true);
    } else {
      toast.error(`Something went wrong`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
    }
  };
  const CustomGroupRowContent = ({ row, column, children }) => {
    // Use the actual data instead of creating a deep copy
    let filterData = data;
    const summary = row.value;
    var count = 0;
    var counterMessage = "";
    var timerMessage = "";
    var timeElapsed = 0;
    var totalPrice = 0;
    var averagePrice = 0;
    let totalProjectedPrice = 0;
    var averageProjectedPrice = 0;
    const key = grouping.findIndex((item) => item.columnName === row.groupedBy);
    const currentLevel = key; // This represents the current nesting level
    var arrCompoundKey = row.compoundKey.split("|");

    // For nested grouping, we need to filter data step by step up to the current level only
    if (currentLevel > 0) {
      // Apply filters for all levels up to but not including the current level
      for (let i = 0; i < currentLevel; i++) {
        const groupColumn = grouping[i].columnName;
        const groupValue = arrCompoundKey[i];
        filterData = filterData.filter((item) => String(item[groupColumn]) === groupValue);
      }
    }

    // Ensure filterData is always an array before calling filter
    if (!Array.isArray(filterData)) {
      filterData = [];
    }

    // Use translatedSelection (which contains indices into the sorted 'data' array) to determine which rows are checked/selected
    // Convert selection indices to actual row IDs using 'data' (the current sorted state shown in the grid)
    const selection = translatedSelection || [];
    const selectedIds = selection.map((index) => data[index]?.id).filter((id) => id !== undefined);
    let groupedRows = filterData.filter((r) => r[row.groupedBy] === row.value && selectedIds.includes(r.id));
    if (groupedRows !== undefined) {
      count = groupedRows.length;
      totalPrice = groupedRows.reduce((acc, item) => {
        if (item.isNegative) {
          return acc + (item.isProfit ? -item.eventPrice : item.eventPrice);
        }
        return acc + (item.isProfit ? item.eventPrice : -item.eventPrice);
      }, 0); // Calculate average price
      averagePrice = count > 0 ? totalPrice / count : 0;
      totalProjectedPrice = groupedRows.reduce((acc, item) => {
        if (item.isNegative) {
          return acc + (item.isProfit ? -item.projectedEventPrice : item.projectedEventPrice);
        }
        return acc + (item.isProfit ? item.projectedEventPrice : -item.projectedEventPrice);
      }, 0); // Calculate average projected price
      averageProjectedPrice = count > 0 ? totalProjectedPrice / count : 0;

      var counterRows = groupedRows.filter((x) => x.typeId === controlTypes.EXTERNALAPI && x.externalApiTypeId === externalApiTypes.NUMERIC);
      var oldCounterRows = groupedRows.filter((x) => x.typeId === controlTypes.COUNTER); // temporary fix for old counting logic, should be removed in the future
      var timerRows = groupedRows.filter((x) => x.typeId === controlTypes.STOPWATCH || (x.typeId === controlTypes.EXTERNALAPI && x.externalApiTypeId === externalApiTypes.STOPWATCH));
      if (counterRows.length > 0 || oldCounterRows.length > 0) {
        var total = 0;
        let counterTotal = 0;
        let oldCounterTotal = 0;

        counterRows.forEach((x) => {
          if (x.isNegative) {
            counterTotal = counterTotal - Math.abs(x.measurementValue);
          } else {
            counterTotal = counterTotal + x.measurementValue;
          }
        });
        let positiveCounterRows = oldCounterRows.filter((x) => !x.isNegative);
        let negativeCounterRows = oldCounterRows.filter((x) => x.isNegative);

        oldCounterTotal = positiveCounterRows.length - negativeCounterRows.length;
        total = counterTotal + oldCounterTotal;
        counterMessage = total; // Use the formatted message
      }
      if (timerRows.length > 0) {
        let total = 0;
        timerRows.forEach((x) => {
          total = total + x.integerValue;
        });
        const averageTime = total / timerRows.length;
        timerMessage = `${formatToTimeString(total)} (avg: ${formatToTimeStringHHMMSS(averageTime)})`;
      }

      // calculate the difference in seconds between the first measurement starttime and last measurement (endtime if stopwatch, starttime if else)
      if (groupedRows.length > 0) {
        // get row with earliest starttime
        const earliestStartTime = groupedRows.reduce((prev, current) => (prev.startTime < current.startTime ? prev : current));
        // get row with latest stoptime
        const latestStopTime = groupedRows.reduce((prev, current) => (prev.stopTime > current.stopTime ? prev : current));
        // get row with latest starttime
        const latestStartTime = groupedRows.reduce((prev, current) => (prev.startTime > current.startTime ? prev : current));

        // check if latest stoptime is later than latest starttime, use the latest one. then use it to calculate the difference between earliestStartTime and latest time
        const latestTime = latestStopTime.stopTime > latestStartTime.startTime ? latestStopTime.stopTime : latestStartTime.startTime;

        timeElapsed = (new Date(latestTime) - new Date(earliestStartTime.startTime)) / 1000;
        timeElapsed = formatToTimeString(timeElapsed);
      }
    }
    const handleSort = (property) => (direction) => (event) => {
      event.stopPropagation();
      const currentLevelSorting = groupSortingByLevel[currentLevel];

      // If the current level already has sorting for this property and direction, reset it
      if (currentLevelSorting && currentLevelSorting.property === property && currentLevelSorting.direction === direction) {
        // Reset sort if clicking the same property and direction for this level
        const newGroupSorting = { ...groupSortingByLevel };
        delete newGroupSorting[currentLevel];
        setGroupSortingByLevel(newGroupSorting);
      } else {
        // Set new sorting for this level
        setGroupSortingByLevel((prev) => ({
          ...prev,
          [currentLevel]: { property, direction },
        }));

        // Clear any DevExtreme sorting on the currently grouped column for this specific level only
        const currentGroupedColumn = row.groupedBy;
        const newSorting = sorting.filter((sort) => sort.columnName !== currentGroupedColumn);
        setSorting(newSorting);
      }
    };

    // Helper function to format price with average
    const formatPriceWithAverage = (totalPrice, averagePrice, currency) => {
      const formatPrice = (price) => {
        return Math.abs(price)
          .toFixed(2)
          .replace(".", ",")
          .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      };

      const totalFormatted = `${currency.symbol}${formatPrice(totalPrice)}`;
      const avgFormatted = `${averagePrice < 0 ? "-" : ""}${currency.symbol}${formatPrice(averagePrice)}`;

      return `${totalFormatted} (avg: ${avgFormatted})`;
    };

    const SortableItem = ({ label, value, width }) => {
      const currentLevelSorting = groupSortingByLevel[currentLevel];
      const isActive = currentLevelSorting && currentLevelSorting.property === label;

      return (
        <span style={{ minWidth: width, flex: "1 1 0" }} className={classes.sortableItem}>
          <b style={{ flexShrink: 0 }}>{label}:</b>
          <span className={classes.sortableValue}>{value}</span>
          <div className={`${classes.sortIcons} ${isActive ? classes.sortIconsActive : ""}`}>
            <ChevronUp onClick={handleSort(label)("asc")} className={isActive && currentLevelSorting.direction === "asc" ? classes.activeIcon : ""} />
            <ChevronDown onClick={handleSort(label)("desc")} className={isActive && currentLevelSorting.direction === "desc" ? classes.activeIcon : ""} />
          </div>
        </span>
      );
    };

    return (
      <TableGroupRow.Content row={row} column={column} sx={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <div className={classes.groupRowContent}>
          <div className={classes.groupRowStats}>
            <div style={{ minWidth: GROUP_STAT_WIDTHS.key, flex: "1 1 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {children !== null ? children : summary}
            </div>
            <SortableItem label="Counter total" value={counterMessage ? counterMessage : "-"} width={GROUP_STAT_WIDTHS.counterTotal} />
            <SortableItem label="Total time" value={timerMessage ? timerMessage : "-"} width={GROUP_STAT_WIDTHS.totalTime} />
            <SortableItem label="Time elapsed" value={timeElapsed ? timeElapsed : "-"} width={GROUP_STAT_WIDTHS.timeElapsed} />
            <span style={{ minWidth: GROUP_STAT_WIDTHS.price, flex: "1 1 0" }} className={classes.sortableItem}>
              <b style={{ flexShrink: 0 }}>Total price:</b>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0, overflow: "hidden", flex: "1 1 auto" }}>
                {totalPrice < 0 ? <Minus size={16} color={"#D84949"} style={{ flexShrink: 0 }} /> : <Plus size={16} color={"#5CC958"} style={{ flexShrink: 0 }} />}
                <p style={{ margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{formatPriceWithAverage(totalPrice, averagePrice, currency)}</p>
              </div>
              <div className={`${classes.sortIcons} ${groupSortingByLevel[currentLevel] && groupSortingByLevel[currentLevel].property === "totalPrice" ? classes.sortIconsActive : ""}`}>
                <ChevronUp
                  onClick={handleSort("totalPrice")("asc")}
                  className={
                    groupSortingByLevel[currentLevel] && groupSortingByLevel[currentLevel].property === "totalPrice" && groupSortingByLevel[currentLevel].direction === "asc" ? classes.activeIcon : ""
                  }
                />
                <ChevronDown
                  onClick={handleSort("totalPrice")("desc")}
                  className={
                    groupSortingByLevel[currentLevel] && groupSortingByLevel[currentLevel].property === "totalPrice" && groupSortingByLevel[currentLevel].direction === "desc" ? classes.activeIcon : ""
                  }
                />
              </div>
            </span>
            {props.activeProjection && (
              <span style={{ minWidth: GROUP_STAT_WIDTHS.projectedPrice, flex: "1 1 0" }} className={classes.sortableItem}>
                <b style={{ flexShrink: 0 }}>Total projected price:</b>
                <div style={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0, overflow: "hidden", flex: "1 1 auto" }}>
                  {totalProjectedPrice < 0 ? <Minus size={16} color={"#D84949"} style={{ flexShrink: 0 }} /> : <Plus size={16} color={"#5CC958"} style={{ flexShrink: 0 }} />}
                  <p style={{ margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{formatPriceWithAverage(totalProjectedPrice, averageProjectedPrice, currency)}</p>
                </div>
                <div className={`${classes.sortIcons} ${groupSortingByLevel[currentLevel] && groupSortingByLevel[currentLevel].property === "totalProjectedPrice" ? classes.sortIconsActive : ""}`}>
                  <ChevronUp
                    onClick={handleSort("totalProjectedPrice")("asc")}
                    className={
                      groupSortingByLevel[currentLevel] && groupSortingByLevel[currentLevel].property === "totalProjectedPrice" && groupSortingByLevel[currentLevel].direction === "asc"
                        ? classes.activeIcon
                        : ""
                    }
                  />
                  <ChevronDown
                    onClick={handleSort("totalProjectedPrice")("desc")}
                    className={
                      groupSortingByLevel[currentLevel] && groupSortingByLevel[currentLevel].property === "totalProjectedPrice" && groupSortingByLevel[currentLevel].direction === "desc"
                        ? classes.activeIcon
                        : ""
                    }
                  />
                </div>
              </span>
            )}
            <span style={{ minWidth: GROUP_STAT_WIDTHS.eventCount, flex: "1 1 0" }} className={classes.sortableItem}>
              <b style={{ flexShrink: 0 }}>Event count:</b>
              <span style={{ minWidth: "30px", display: "flex" }}>{count}</span>
              <div className={`${classes.sortIcons} ${groupSortingByLevel[currentLevel] && groupSortingByLevel[currentLevel].property === "eventCount" ? classes.sortIconsActive : ""}`}>
                <ChevronUp
                  onClick={handleSort("eventCount")("asc")}
                  className={
                    groupSortingByLevel[currentLevel] && groupSortingByLevel[currentLevel].property === "eventCount" && groupSortingByLevel[currentLevel].direction === "asc" ? classes.activeIcon : ""
                  }
                />
                <ChevronDown
                  onClick={handleSort("eventCount")("desc")}
                  className={
                    groupSortingByLevel[currentLevel] && groupSortingByLevel[currentLevel].property === "eventCount" && groupSortingByLevel[currentLevel].direction === "desc" ? classes.activeIcon : ""
                  }
                />
              </div>
            </span>
          </div>
        </div>
      </TableGroupRow.Content>
    );
  };

  const groupByColumnName = (array, columnName) => {
    //Function to group all measurments by the given columnName
    return array.reduce((result, item) => {
      const key = item[columnName]; // Dynamically get the key value
      if (!result[key]) {
        result[key] = []; // Initialize the group if it doesn't exist
      }
      result[key].push(item); // Add the current item to the group
      return result;
    }, {});
  };

  const handleDialogClose = () => {
    setImageDialogOpen(false);
  };

  const formatlessSummaryTypes = ["customTotal"];
  const messages = {
    customTotal: "Price higher than $6000",
    count: "count",
  };

  return (
    <React.Fragment>
      <div id="taplrDevExTable" ref={tableWrapperRef}>
        <Grid rows={data} columns={props.columns}>
          <DragDropProvider />

          {/* DataTypeProviders are used to format the data, like dates or the valuetype (we show these as a pill) */}
          <DataTypeProvider formatterComponent={startTimeFormatter} for={["startTime"]} availableFilterOperations={["contains"]} />
          <DataTypeProvider for={["durationInMinutes"]} availableFilterOperations={["equal", "notEqual", "greaterThan", "greaterThanOrEqual", "lessThan", "lessThanOrEqual"]} />
          <DataTypeProvider
            formatterComponent={startHourFormatter}
            for={["startHour"]}
            availableFilterOperations={["equal", "notEqual", "greaterThan", "greaterThanOrEqual", "lessThan", "lessThanOrEqual"]}
          />
          <DataTypeProvider formatterComponent={startDayOfWeekFormatter} for={["startDayOfWeek"]} availableFilterOperations={["contains"]} />
          <DataTypeProvider formatterComponent={finishedFormatter} for={["running"]} availableFilterOperations={["contains"]} />
          <DataTypeProvider formatterComponent={valueFormatter} for={["measurementValue", "keyValue", "commentText"]} availableFilterOperations={["contains"]} />
          <DataTypeProvider formatterComponent={valueTypeFormatter} for={["valueType"]} availableFilterOperations={["contains"]} />
          <DataTypeProvider
            formatterComponent={priceFormatter}
            for={["signedEventPrice"]}
            availableFilterOperations={["equal", "notEqual", "greaterThan", "greaterThanOrEqual", "lessThan", "lessThanOrEqual"]}
          />
          {/* no filters on action */}
          <DataTypeProvider formatterComponent={actionFormatter} for={["action"]} />
          <DataTypeProvider for={["companyName", "projectName", "resourceName", "description"]} availableFilterOperations={["contains"]} />
          <DataTypeProvider formatterComponent={sessionValuesFormatter} for={["sessionValues"]} availableFilterOperations={["contains"]} />

          <FilteringState filters={props.currentFilters || []} columnExtensions={filteringStateColumnExtensions} onFiltersChange={props.handleFiltersChange} />
          <SortingState
            sorting={sorting}
            onSortingChange={(newSorting) => {
              handleSortingChange(newSorting);
              // Step 12: in remote mode, notify the parent so it can refetch with the new sort.
              if (props.remoteOperations && props.onRemoteSortChange) props.onRemoteSortChange(newSorting);
            }}
            columnExtensions={resultTableColumnExtensions}
          />
          <GroupingState grouping={grouping} onGroupingChange={handleGroupingChange} columnExtensions={resultGroupingColumnExtensions} showGroupingControls />
          <SummaryState />
          {/* Step 12 (Phase F): remote paging opt-in — parent controls page index and total count. */}
          {props.remoteOperations
            ? <PagingState currentPage={props.remoteCurrentPage || 0} pageSize={props.remotePageSize || 100} onCurrentPageChange={props.onPageChange || (() => {})} />
            : <PagingState defaultPageSize={100} />}
          <SelectionState selection={translatedSelection} onSelectionChange={handleSelectionChange} mode="multiple" deferred={true} />

          <IntegratedFiltering />
          <IntegratedGrouping />
          <IntegratedSummary messages={messages} />
          {!props.remoteOperations && <IntegratedSorting />}
          {props.remoteOperations
            ? <CustomPaging totalCount={props.remoteTotalCount || 0} />
            : <IntegratedPaging />}
          <IntegratedSelection getRowId={(row) => row.id} />

          <VirtualTable columnExtensions={resultTableColumnExtensions} messages={tableMessages} />
          <TableColumnResizing columnWidths={columnWidths} onColumnWidthsChange={handleColumnWidthsChange} resizingMode="nextColumn" />
          <TableFilterRow showFilterSelector={true} />
          <TableHeaderRow showSortingControls columnExtensions={resultTableColumnExtensions} />
          <TableSelection showSelectAll enabled={true} rowSelectionEnabled={true} />
          <TableGroupRow contentComponent={CustomGroupRowContent} showColumnsWhenGrouped />
          <TableColumnVisibility hiddenColumnNames={hiddenColumnNames} onHiddenColumnNamesChange={handleHiddenColumnNamesChange} />

          <TableSummaryRow formatlessSummaryTypes={formatlessSummaryTypes} />

          <Toolbar />
          <GroupingPanel showSortingControls={true} showGroupingControls></GroupingPanel>
          <ColumnChooser />

          <PagingPanel
            pageSizes={100}
            messages={{
              showAll: "Show all",
              rowsPerPage: "Rows per page",
              info: "{from} of {to} ({count} elements)",
            }}
          />
          <ExportPanel startExport={startExport} />
        </Grid>
        <GridExporter
          ref={exporterRef}
          rows={data}
          columns={resultColumns}
          grouping={grouping}
          // groupSummaryItems={groupSummaryItems}
          selection={translatedSelection}
          onSave={onSave} // Ensure onSave is properly passed
        />
      </div>
      {imageDialogOpen && (
        <Dialog open={imageDialogOpen} onClose={handleDialogClose}>
          <DialogTitle>{description}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <img alt="" style={{ maxWidth: "100%", maxHeight: "calc(100vh - 64px)" }} src={"data:image/png;base64," + image}></img>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ padding: "0px 24px 10px 0px" }}>
            <Button autoFocus content="Close" className={classes.closeButton} onClick={handleDialogClose}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </React.Fragment>
  );
}

const useStyles = makeStyles(() => ({
  valueStyle: {
    whiteSpace: "wrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
    maxWidth: "250px",
  },
  closeButton: {
    gap: "8px",
    backgroundColor: "#0B4097 !important",
    border: "1px solid #0B4097 !important",
    color: "white !important",
    "&:hover": {
      background: "white !important",
      color: "#0B4097 !important",
    },
    "&:disabled": {
      background: "#bdb6ac !important",
      border: "1px solid grey !important",
      color: "white !important",
      cursor: "not-allowed",
    },
  },
  tableFooter: {
    width: "100%",
  },
  footer: {
    display: "flex",
  },
  tableHeadRow: {},
  tableHead: {
    height: "42px",
    borderRight: "2px solid #e6ecf0",
  },
  titleSearch: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  icons: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  imageStyle: {
    marginTop: "15px",
    height: "100px",
    width: "100px",
  },

  chip__grey: {
    width: "fit-content",
    height: "24px",
    display: "inline-flex",
    alignItems: "center",
    padding: "0px 12px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    borderRadius: "12px",
    backgroundColor: "#EDEDED",
    color: "#7C7C7C",
  },
  chip__green: {
    width: "fit-content",
    height: "24px",
    display: "inline-flex",
    alignItems: "center",
    padding: "0px 12px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    borderRadius: "12px",
    backgroundColor: "#E2F8E0",
    color: "#3AB037",
  },
  chip__orange: {
    width: "fit-content",
    height: "24px",
    display: "inline-flex",
    alignItems: "center",
    padding: "0px 12px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    borderRadius: "12px",
    backgroundColor: "#FEF1D6",
    color: "#E76F0F",
  },

  chip__red: {
    width: "fit-content",
    height: "24px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0px 12px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    borderRadius: "12px",
    backgroundColor: "#FBE5E5",
    color: "#D84949",
  },

  eventPrice: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "rgba(12, 14, 41, 1)",
    margin: "0px",
    textAlign: "right",
  },

  emptyStateCard: {
    maxWidth: "320px",
    boxSizing: "border-box",
    padding: "16px",
    margin: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: "transparent",
  },

  emptyStateCard__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    color: "rgba(12, 14, 41, 1)",
    margin: "0px",
    marginBottom: "8px",
  },

  emptyStateCard__subtitle: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "rgba(12, 14, 41, 0.8)",
    textAlign: "center",
    margin: "0px",
  },
  sortableItem: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    gap: "4px",
    "&:hover": {
      "& $sortIcons": {
        opacity: 1,
      },
    },
  },
  sortableValue: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: "1 1 auto",
  },
  sortIcons: {
    display: "flex",
    flexDirection: "column",
    opacity: 0,
    marginLeft: "8px",
    transition: "opacity 0.2s ease-in-out",
    "& svg": {
      width: "14px",
      height: "14px",
      transition: "color 0.2s ease-in-out",
      color: "#7C7C7C",
    },
    "& svg:hover": {
      color: "#0B4097",
    },
  },
  sortIconsActive: {
    opacity: 1,
  },
  activeIcon: {
    "&&": {
      color: "#0B4097",
    },
  },
  groupRowContent: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    paddingLeft: "5px",
  },
  groupRowStats: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "20px",
    // When the fixed-width stats are wider than the available space, scroll them
    // horizontally instead of letting the values overlap.
    flexWrap: "nowrap",
    overflowX: "auto",
    overflowY: "hidden",
    paddingBottom: "2px",
  },
}));

// Fixed pixel widths for each group-summary stat so a given stat (e.g. "Counter
// total") is rendered at the same width in every group rather than sizing to its
// content. Shared by all DevExpress grouped tables to keep them consistent.
const GROUP_STAT_WIDTHS = {
  key: "200px",
  counterTotal: "170px",
  totalTime: "250px",
  timeElapsed: "180px",
  price: "250px",
  projectedPrice: "270px",
  eventCount: "150px",
};
